/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogOpenChangeData,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    Input,
    Textarea,
    makeStyles,
} from '@fluentui/react-components';
import { CalendarAddRegular, CalendarEditRegular } from '@fluentui/react-icons';
import { Column, Row } from '../common';
import { useAccountState } from '../../redux/reducers/accountReducer';
import { TimeTrackingEntry, useInitialValues, UserAccount } from '../../data/types';
import { useValidation } from '../../data/types/validation';
import { useDialogControls } from './useDialogControls';

const useStyles = makeStyles({
    root: {},
    field: {
        width: '100%',
    },
    descrip: {
        height: '4rem',
    },
    hoursInput: {
        width: '8rem',
        height: '4rem',
        fontSize: '42px',
    },
});
export type AddEditTimeTrackEntryProps = {
    caseId?: string;
    onSubmit?: (values: any) => Promise<void>;
    timeTrackEntryToEdit?: TimeTrackingEntry;
    children?: JSX.Element;
    entryDate?: Date;
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
};
export const AddEditTimeTrackEntryDialog = (props: AddEditTimeTrackEntryProps) => {
    const { caseId, entryDate, children, onSubmit, timeTrackEntryToEdit, open, onOpenChange } = props;

    //Hooks
    const { primaryAccount } = useAccountState();
    const { user } = (primaryAccount as UserAccount) || {};
    const initValues = useInitialValues();
    const { validateTimeTrackEntry } = useValidation();

    //Styles
    const styles = useStyles();

    //Form Stuff
    const initTimeEntry = () => {
        return initValues.timeTrackEntry(caseId as string, user?.userId as string, undefined, entryDate);
    };
    const timeEntryForm = useFormik({
        initialValues: !!timeTrackEntryToEdit ? { ...timeTrackEntryToEdit } : initTimeEntry(),
        onSubmit: async (values, { resetForm }) => {
            const timeEntry = values as TimeTrackingEntry;
            if (isNaN(timeEntry.hours)) {
                timeEntry.hours = 0;
            }

            if (!!timeEntry.hours && !!timeEntry.description) {
                resetForm({ values: !!timeTrackEntryToEdit ? { ...values } : initTimeEntry() });

                !!onSubmit && (await onSubmit(values));
            }
        },
        validationSchema: validateTimeTrackEntry.schema,
    });

    //Dialog Controls
    const [dialogOpen, onOpenChangeHandler] = useDialogControls(open, onOpenChange);

    //UI Events
    const clickSubmit = (e: any) => {
        e.stopPropagation();

        timeEntryForm.handleSubmit();
    };
    const clickCancel = (e: any) => {
        e.stopPropagation();

        timeEntryForm.resetForm({
            values: !!timeTrackEntryToEdit ? { ...timeTrackEntryToEdit } : initTimeEntry(),
        });
    };
    const onHoursChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        const hours = e.target.value;
        timeEntryForm.setFieldValue('hours', Number(Number(hours).toFixed(2)));
    };

    //Effects
    useEffect(() => {
        if (dialogOpen && (timeTrackEntryToEdit || (caseId && entryDate))) {
            timeEntryForm.resetForm({
                values: !!timeTrackEntryToEdit ? { ...timeTrackEntryToEdit } : initTimeEntry(),
            });
        }
    }, [dialogOpen, caseId, entryDate, timeTrackEntryToEdit]);

    return (
        <Dialog
            open={dialogOpen}
            onOpenChange={onOpenChangeHandler}>
            <DialogTrigger disableButtonEnhancement>{children}</DialogTrigger>
            <DialogSurface aria-describedby={undefined} mountNode={undefined}>
                <DialogBody>
                    <DialogTitle>
                        <Row gap='medium'>
                            <Column width='auto' vAlign='middle'>
                                {!!timeTrackEntryToEdit ? <CalendarEditRegular /> : <CalendarAddRegular />}
                            </Column>
                            <Column>
                                {`${!!timeTrackEntryToEdit ? 'Edit' : 'Add'} Time Entry for ${new Date(entryDate!).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`}
                            </Column>
                        </Row>
                    </DialogTitle>
                    <DialogContent>
                        <Row padding='py-3'>
                            <Column>
                                <Field
                                    className={styles.field}
                                    label='Description'
                                    required
                                    validationMessage={timeEntryForm.errors.description}
                                    hint={`${timeEntryForm.values.description?.length || 0} / ${validateTimeTrackEntry.description.maxLength}`}
                                >
                                    <Textarea
                                        className={styles.descrip}
                                        name='description'
                                        value={timeEntryForm.values.description}
                                        onChange={timeEntryForm.handleChange}
                                        placeholder='Enter description of work'
                                        data-testid='timeentry-dialog-description'
                                        maxLength={validateTimeTrackEntry.description.maxLength}
                                    />
                                </Field>
                            </Column>
                            <Column width='auto'>
                                <Field className={styles.field} label='Hours' required validationMessage={timeEntryForm.errors.hours}>
                                    <Input
                                        className={styles.hoursInput}
                                        name='hours'
                                        value={`${timeEntryForm.values.hours || ''}`}
                                        onChange={timeEntryForm.handleChange}
                                        onBlur={onHoursChanged}
                                        placeholder='0.00'
                                        data-testid='timeentry-dialog-hours'
                                    />
                                </Field>
                            </Column>
                        </Row>
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                type='submit'
                                appearance='primary'
                                onClick={clickSubmit}
                                data-testid='timeentry-dialog-submit'
                                disabled={!timeEntryForm.dirty || !timeEntryForm.isValid}
                            >
                                Submit Time Entry
                            </Button>
                        </DialogTrigger>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance='secondary' onClick={clickCancel} data-testid='timeentry-dialog-cancel'>
                                Cancel
                            </Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
